import { z } from "zod";

const slugSchema = z
  .string()
  .transform((val) => (val.trim() === "" ? undefined : val.trim()))
  .pipe(
    z
      .string()
      .min(2, "Slug must be at least 2 characters")
      .max(200, "Slug must be at most 200 characters")
      .regex(/^[a-z0-9-]+$/, "Slug must be lowercase letters, numbers, and hyphens only")
      .optional(),
  );

const episodeStatusSchema = z.enum(["draft", "scheduled", "published"]);
const episodeTypeSchema = z.enum(["full", "trailer", "bonus"]);

const chapterSchema = z.object({
  startTime: z.coerce.number().min(0, "Start time must be positive"),
  title: z.string().min(1, "Chapter title is required").max(200, "Chapter title too long"),
});

export const createEpisodeSchema = z.object({
  title: z
    .string()
    .min(2, "Episode title must be at least 2 characters")
    .max(500, "Episode title must be at most 500 characters"),
  slug: slugSchema,
  description: z.string().max(5000, "Description must be at most 5000 characters").optional(),
  showNotes: z.string().max(50000, "Show notes must be at most 50000 characters").optional(),
  audioUrl: z.string().url("Must be a valid URL").max(500).optional().or(z.literal("")),
  audioDurationSeconds: z.coerce.number().int().min(0).optional(),
  audioFileSizeBytes: z.coerce.number().int().min(0).optional(),
  transcriptUrl: z.string().url("Must be a valid URL").max(500).optional().or(z.literal("")),
  status: episodeStatusSchema.default("draft"),
  isPremium: z.coerce.boolean().default(false),
  seasonNumber: z.coerce.number().int().min(1).max(9999).optional().or(z.literal("")),
  episodeNumber: z.coerce.number().int().min(1).max(99999).optional().or(z.literal("")),
  episodeType: episodeTypeSchema.default("full"),
  explicit: z.coerce.boolean().default(false),
  chapters: z.array(chapterSchema).optional(),
  scheduledAt: z.string().optional(),
});

export const updateEpisodeSchema = z.object({
  title: z
    .string()
    .min(2, "Episode title must be at least 2 characters")
    .max(500, "Episode title must be at most 500 characters")
    .optional(),
  slug: slugSchema,
  description: z.string().max(5000).nullable().optional(),
  showNotes: z.string().max(50000).nullable().optional(),
  audioUrl: z.string().url("Must be a valid URL").max(500).nullable().optional(),
  audioDurationSeconds: z.coerce.number().int().min(0).nullable().optional(),
  audioFileSizeBytes: z.coerce.number().int().min(0).nullable().optional(),
  transcriptUrl: z.string().url("Must be a valid URL").max(500).nullable().optional(),
  status: episodeStatusSchema.optional(),
  isPremium: z.coerce.boolean().optional(),
  seasonNumber: z.coerce.number().int().min(1).max(9999).nullable().optional(),
  episodeNumber: z.coerce.number().int().min(1).max(99999).nullable().optional(),
  episodeType: episodeTypeSchema.optional(),
  explicit: z.coerce.boolean().optional(),
  chapters: z.array(chapterSchema).nullable().optional(),
  scheduledAt: z.string().nullable().optional(),
});

export type CreateEpisodeFormData = z.infer<typeof createEpisodeSchema>;
export type UpdateEpisodeFormData = z.infer<typeof updateEpisodeSchema>;
